/**
 * Signed public-key bundles — the L2 mechanism of Public Key Authentication
 * (spec §5.2). A user signs every encryption public key they publish with their
 * Ed25519 identity private key; consumers verify the signature against the
 * signer's identity public key before wrapping any secret to that key. This
 * reduces "authenticate every encryption key" to "authenticate one identity key
 * per person" (which L3 handles via TOFU + safety-number verification).
 *
 * `safetyNumber` derives the human-comparable fingerprint two users read to each
 * other out-of-band to verify they hold each other's genuine identity key.
 *
 * Ed25519 (RFC 8032) is deterministic, so signatures produced here interoperate
 * byte-for-byte with any correct implementation on the native clients. The ONE
 * thing every client must reproduce identically is `canonicalBytes` below — the
 * exact bytes that get signed. It is pinned by a test vector in signedKey.test.ts.
 */
import { ed25519 } from '@noble/curves/ed25519.js';
import { bytesToBase64Url, base64UrlToBytes } from './encoding';
import { SAFETY_WORDS } from './safetyWords';

/** What kind of encryption key a signature vouches for. */
export type SignedKeyType = 'account-x25519' | 'family-x25519' | 'recovery-reset-x25519';

export interface SignedKey {
  /** The encryption public key being authenticated. base64url(32 bytes). */
  key: string;
  keyType: SignedKeyType;
  /** Whose key / which family this is (userId or orgId). */
  subject: string;
  /** Extra binding (e.g. orgId for a family key, resetId for a reset ephemeral). '' when unused. */
  context: string;
  /** ISO-8601 issuance time. */
  notBefore: string;
  /** Whose identity key signed. Equals `subject` for a self-signed account key. */
  signerId: string;
  /** Ed25519 signature over canonicalBytes(fields). base64url(64 bytes). */
  sig: string;
}

/** Fields covered by the signature (everything in SignedKey except `sig`). */
export type SignedKeyFields = Omit<SignedKey, 'sig'>;

const DOMAIN_TAG = 'hbs-pka-v1';

/**
 * The exact bytes that are signed/verified. MUST be identical across every
 * client. Field order is fixed (JSON.stringify preserves insertion order for
 * string keys) and prefixed with a domain tag so a signature can never be
 * repurposed outside PKA. Do not change without bumping the domain tag and
 * migrating — the test vector guards this.
 */
export function canonicalBytes(f: SignedKeyFields): Uint8Array {
  const canon = JSON.stringify({
    key: f.key,
    keyType: f.keyType,
    subject: f.subject,
    context: f.context,
    notBefore: f.notBefore,
    signerId: f.signerId,
  });
  return new TextEncoder().encode(DOMAIN_TAG + canon);
}

/** Sign a key bundle with the signer's Ed25519 identity private key (32-byte seed). */
export function signKey(identityPrivateKey: Uint8Array, fields: SignedKeyFields): SignedKey {
  const sig = ed25519.sign(canonicalBytes(fields), identityPrivateKey);
  return { ...fields, sig: bytesToBase64Url(sig) };
}

/**
 * Verify a signed key bundle against the signer's identity public key (32 bytes).
 * Returns false on any malformed input or signature mismatch — never throws.
 */
export function verifyKey(identityPublicKey: Uint8Array, signed: SignedKey): boolean {
  try {
    const { sig, ...fields } = signed;
    return ed25519.verify(base64UrlToBytes(sig), canonicalBytes(fields), identityPublicKey);
  } catch {
    return false;
  }
}

/** A family's published encryption public key plus the fields needed to
 *  authenticate it (PKA sites A1/A2/A3). Served by the untrusted server. */
export interface FamilyKeyClaim {
  /** orgId. */
  id: string;
  /** The family X25519 public key, base64url(32 bytes). */
  publicKey: string;
  /** The founder (whose identity key must have signed the family key). */
  adminUserId: string;
  /** The founder's signature over `publicKey`. Optional in the wire type, but a
   *  claim without it is rejected by `verifyFamilyKeySignature` (no grandfathering). */
  publicKeySig?: SignedKey;
  /** The founder's identity public key (base64url), served for verification. */
  adminIdentityPublicKey?: string;
}

/**
 * Verify the founder's signature over a family public key — the pure,
 * storage-free half of family-key authentication, shared by web and extension.
 *
 * Returns true ONLY for a valid founder signature: right keyType, signed by the
 * founder over exactly this public key, bound to this org, and verifying against
 * the served founder identity key. Returns false when the signature is absent
 * (universal enforcement — grandfathering was retired; a missing signature is a
 * downgrade attempt, not a pre-PKA family), or present but the founder identity
 * is missing or the signature does not verify.
 *
 * Callers still pin the founder identity (TOFU) before trusting the key — that
 * step is client-specific (localStorage on web, chrome.storage on the
 * extension), so it lives in each client's `familyKeyAuthentic` wrapper.
 */
export function verifyFamilyKeySignature(org: FamilyKeyClaim): boolean {
  // No signature → reject. A malicious server stripping publicKeySig to force a
  // silent downgrade fails here; there are no unsigned families to admit.
  if (!org.publicKeySig) return false;

  const adminIdPub = org.adminIdentityPublicKey;
  const sig = org.publicKeySig;
  return !!adminIdPub
    && sig.keyType === 'family-x25519'
    && sig.signerId === org.adminUserId
    && sig.subject === org.id
    && sig.key === org.publicKey
    && verifyKey(base64UrlToBytes(adminIdPub), sig);
}

/**
 * The order-independent digest underlying BOTH renderings of the identity-pair
 * fingerprint (`safetyNumber` and `safetyWords`). Extracted so the two cannot
 * drift apart: they must stay two views of one value, or a user who verified
 * via words and a user who verified via digits would not be comparing the same
 * thing. Inputs are sorted so both sides compute the same digest regardless of
 * who is "a".
 */
async function pairDigest(
  aIdentityPublicKey: Uint8Array,
  aUserId: string,
  bIdentityPublicKey: Uint8Array,
  bUserId: string,
): Promise<Uint8Array> {
  const a = `${aUserId}:${bytesToBase64Url(aIdentityPublicKey)}`;
  const b = `${bUserId}:${bytesToBase64Url(bIdentityPublicKey)}`;
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  const material = new TextEncoder().encode(`${DOMAIN_TAG}|${lo}|${hi}`);
  return new Uint8Array(await crypto.subtle.digest('SHA-256', material as BufferSource));
}

/**
 * A stable, human-comparable safety number for the identity pair (a, b). Both
 * sides compute the same value regardless of argument order (inputs are sorted),
 * so two people can read it to each other to confirm they hold each other's
 * genuine identity key. 60 decimal digits, grouped into 5-digit blocks by the UI.
 */
export async function safetyNumber(
  aIdentityPublicKey: Uint8Array,
  aUserId: string,
  bIdentityPublicKey: Uint8Array,
  bUserId: string,
): Promise<string> {
  const digest = await pairDigest(aIdentityPublicKey, aUserId, bIdentityPublicKey, bUserId);
  // Turn the digest into 60 decimal digits: 12 blocks of 5, each from 5 bytes.
  let out = '';
  for (let block = 0; block < 12; block++) {
    let acc = 0;
    for (let j = 0; j < 5; j++) acc = (acc * 256 + digest[(block * 5 + j) % digest.length]) % 100000;
    out += acc.toString().padStart(5, '0');
  }
  return out;
}

/**
 * A short human-comparable fingerprint of a recovery-reset ephemeral public key
 * (PKA A5). The reset requester is locked out of their identity key mid-reset, so
 * the ephemeral can't be identity-signed; instead the requester reads this
 * fingerprint to the approving contact out-of-band, and the contact's client
 * checks it against the ephemeral the server served. A substituted ephemeral
 * produces a different fingerprint, so the humans catch it. 30 digits — a
 * one-shot per-reset value, shorter than the long-term identity safety number.
 */
export async function ephemeralFingerprint(ephemeralPublicKey: Uint8Array): Promise<string> {
  const material = new TextEncoder().encode(`${DOMAIN_TAG}|reset-ephemeral|${bytesToBase64Url(ephemeralPublicKey)}`);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', material as BufferSource));
  let out = '';
  for (let block = 0; block < 6; block++) {
    let acc = 0;
    for (let j = 0; j < 5; j++) acc = (acc * 256 + digest[block * 5 + j]) % 100000;
    out += acc.toString().padStart(5, '0');
  }
  return out;
}

/** How many words a safety fingerprint is rendered as. Six words over a
 *  1024-word list is 60 bits — enough that grinding an identity keypair to
 *  collide with someone else's fingerprint is out of reach, while still being
 *  something two people will actually read to each other. */
export const SAFETY_WORD_COUNT = 6;

/**
 * Renders a digest as `SAFETY_WORD_COUNT` words from `SAFETY_WORDS`.
 *
 * Each word consumes exactly 10 bits (1024 = 2^10), read from the digest as a
 * big-endian bit stream. No modulo, so every word is uniformly distributed —
 * a modulo over a non-power-of-two list would bias the low indices and quietly
 * cost entropy.
 */
function wordsFromDigest(digest: Uint8Array, count: number): string[] {
  const out: string[] = [];
  let bitPos = 0;
  for (let i = 0; i < count; i++) {
    let index = 0;
    for (let b = 0; b < 10; b++, bitPos++) {
      const byte = digest[bitPos >> 3];
      const bit = (byte >> (7 - (bitPos & 7))) & 1;
      index = (index << 1) | bit;
    }
    out.push(SAFETY_WORDS[index]);
  }
  return out;
}

/**
 * The spoken form of the identity-pair fingerprint: six words both sides see
 * identically, regardless of argument order.
 *
 * Derived from the same domain-separated digest as `safetyNumber`, so the two
 * renderings are different views of one value rather than independent secrets.
 */
export async function safetyWords(
  aIdentityPublicKey: Uint8Array,
  aUserId: string,
  bIdentityPublicKey: Uint8Array,
  bUserId: string,
): Promise<string[]> {
  return wordsFromDigest(await pairDigest(aIdentityPublicKey, aUserId, bIdentityPublicKey, bUserId), SAFETY_WORD_COUNT);
}

/**
 * The spoken form of the reset-ephemeral fingerprint (PKA A5). Same six-word
 * rendering as `safetyWords` so a user learns one comparison ritual, not two.
 */
export async function ephemeralWords(ephemeralPublicKey: Uint8Array): Promise<string[]> {
  const material = new TextEncoder().encode(`${DOMAIN_TAG}|reset-ephemeral|${bytesToBase64Url(ephemeralPublicKey)}`);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', material as BufferSource));
  return wordsFromDigest(digest, SAFETY_WORD_COUNT);
}
